import { useEffect, useState } from "react";
import api, { moneyExact } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Modal } from "../admin/Projects";
import { Receipt, CreditCard, CheckCircle2, History } from "lucide-react";

function statusBadge(status) {
  if (status === "paid") return "badge-green";
  if (status === "partial") return "badge-rust";
  return "";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function PortalInvoices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paying, setPaying] = useState(false);

  const load = () => api.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openInvoice = async (inv) => {
    setOpen(inv);
    setPayments([]);
    try {
      const { data } = await api.get(`/invoices/${inv.id}/payments`);
      setPayments(data);
    } catch {
      // ignore — payments list is non-critical
    }
  };

  const pay = async (inv, amount) => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        invoice_id: inv.id,
        origin_url: window.location.origin,
        amount: amount,  // null means full balance
      });
      window.location.href = data.url;
    } catch (e) {
      alert("Could not start payment: " + (e.response?.data?.detail || e.message));
      setPaying(false);
    }
  };

  return (
    <DashboardLayout role="client" title="Your Invoices" subtitle="Pay outstanding invoices securely with Stripe. Pay a deposit now or settle in full.">
      {items.length === 0 ? (
        <div className="card p-12 text-center text-sm" style={{color:'var(--text-muted)'}}>
          <Receipt className="mx-auto mb-3"/>No invoices yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((i) => {
            const paid = Number(i.amount_paid || 0);
            const balance = Math.max(0, Number(i.total) - paid);
            const deposit = Number(i.deposit_amount || 0);
            const depositOutstanding = deposit > 0 && paid < deposit ? Math.min(deposit - paid, balance) : 0;
            return (
              <div key={i.id} className="card p-5" data-testid={`portal-inv-${i.id}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="font-display text-lg font-bold">{i.title}</div>
                  <span className={`badge ${statusBadge(i.status)}`}>{i.status}</span>
                </div>
                <div className="text-xs mb-4" style={{color:'var(--text-muted)'}}>Due: {i.due_date || "—"}</div>

                {paid > 0 ? (
                  <div className="space-y-1.5 mb-4">
                    <div className="flex justify-between text-sm" style={{color:'var(--text-muted)'}}>
                      <span>Total</span><span>{moneyExact(i.total)}</span>
                    </div>
                    <div className="flex justify-between text-sm" style={{color:'#86efac'}}>
                      <span>Paid</span><span>− {moneyExact(paid)}</span>
                    </div>
                    <div className="flex justify-between font-display text-2xl font-bold pt-2 border-t border-white/5" style={{color:'var(--accent)'}}>
                      <span className="text-xs uppercase tracking-wider self-end mb-1" style={{color:'var(--text-muted)'}}>Balance</span>
                      <span>{moneyExact(balance)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-2xl font-display font-bold mb-4" style={{color:'var(--accent)'}}>{moneyExact(i.total)}</div>
                )}

                <div className="flex flex-col gap-2">
                  <button className="btn-ghost !py-2 !px-4 !text-sm justify-center" onClick={() => openInvoice(i)} data-testid={`view-inv-${i.id}`}>View details</button>
                  {i.status !== "paid" && balance > 0 && (
                    <div className="flex gap-2">
                      {depositOutstanding > 0 && (
                        <button
                          className="btn-ghost !py-2 !px-3 !text-sm flex-1 justify-center"
                          style={{borderColor:'var(--accent)', color:'var(--accent)'}}
                          disabled={paying}
                          onClick={() => pay(i, depositOutstanding)}
                          data-testid={`pay-deposit-${i.id}`}
                        >
                          <CreditCard size={14}/> Pay Deposit ({moneyExact(depositOutstanding)})
                        </button>
                      )}
                      <button
                        className="btn-primary !py-2 !px-3 !text-sm flex-1 justify-center"
                        disabled={paying}
                        onClick={() => pay(i, null)}
                        data-testid={`pay-full-${i.id}`}
                      >
                        <CreditCard size={14}/> {paid > 0 ? `Pay Balance (${moneyExact(balance)})` : `Pay Full (${moneyExact(balance)})`}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (() => {
        const paid = Number(open.amount_paid || 0);
        const balance = Math.max(0, Number(open.total) - paid);
        const deposit = Number(open.deposit_amount || 0);
        const depositOutstanding = deposit > 0 && paid < deposit ? Math.min(deposit - paid, balance) : 0;
        return (
          <Modal onClose={() => setOpen(null)} title={open.title}>
            <div className="text-sm space-y-3 mb-5">
              <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Status</span><span className={`badge ${statusBadge(open.status)}`}>{open.status}</span></div>
              <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Due</span><span>{open.due_date || '—'}</span></div>
            </div>
            <div className="border-t border-b border-white/5 py-4 my-4">
              <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wider mb-2" style={{color:'var(--text-muted)'}}>
                <div className="col-span-5">Item</div>
                <div className="col-span-2">Qty</div>
                <div className="col-span-2 text-right">Unit Price</div>
                <div className="col-span-3 text-right">Amount</div>
              </div>
              {open.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 py-2 text-sm border-t border-white/5">
                  <div className="col-span-5">
                    <div>{li.description}</div>
                    {(li.item_type || li.color) && (
                      <div className="text-[10px] uppercase tracking-wider mt-0.5 flex gap-2" style={{color:'var(--text-muted)'}}>
                        {li.item_type && <span>{li.item_type.replace('_', ' & ')}</span>}
                        {li.color && <span>· {li.color}</span>}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">{li.quantity}{li.unit ? ` ${li.unit}` : ''}</div>
                  <div className="col-span-2 text-right">{moneyExact(li.unit_price)}</div>
                  <div className="col-span-3 text-right font-semibold">{moneyExact(li.amount)}</div>
                </div>
              ))}
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Subtotal</span><span>{moneyExact(open.subtotal)}</span></div>
              <div className="flex justify-between"><span style={{color:'var(--text-muted)'}}>Tax</span><span>{moneyExact(open.tax)}</span></div>
              <div className="flex justify-between font-display text-xl font-bold pt-3 border-t border-white/5"><span>Total</span><span>{moneyExact(open.total)}</span></div>
              {paid > 0 && (
                <>
                  <div className="flex justify-between" style={{color:'#86efac'}}><span>Paid to date</span><span>− {moneyExact(paid)}</span></div>
                  <div className="flex justify-between font-display text-2xl font-bold pt-2" style={{color:'var(--accent)'}}><span>Outstanding</span><span>{moneyExact(balance)}</span></div>
                </>
              )}
            </div>

            {/* Payment history */}
            {payments.length > 0 && (
              <div className="mt-6 pt-5 border-t border-white/5">
                <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider font-semibold" style={{color:'var(--text-muted)'}}>
                  <History size={13}/> Payment history
                </div>
                <div className="space-y-2" data-testid="payment-history">
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 p-3 rounded-md text-sm" style={{background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.18)'}}>
                      <CheckCircle2 size={14} style={{color:'#86efac'}}/>
                      <div className="flex-1">
                        <div className="font-semibold" style={{color:'#86efac'}}>{moneyExact(p.amount)}</div>
                        <div className="text-xs" style={{color:'var(--text-muted)'}}>Paid on {fmtDate(p.paid_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 justify-end mt-6">
              <button className="btn-ghost" onClick={() => setOpen(null)}>Close</button>
              {open.status !== "paid" && balance > 0 && (
                <>
                  {depositOutstanding > 0 && (
                    <button
                      className="btn-ghost"
                      style={{borderColor:'var(--accent)', color:'var(--accent)'}}
                      disabled={paying}
                      onClick={() => pay(open, depositOutstanding)}
                      data-testid="modal-pay-deposit"
                    >
                      <CreditCard size={15}/> Pay Deposit · {moneyExact(depositOutstanding)}
                    </button>
                  )}
                  <button
                    className="btn-primary"
                    disabled={paying}
                    onClick={() => pay(open, null)}
                    data-testid="modal-pay-full"
                  >
                    <CreditCard size={15}/> {paid > 0 ? `Pay Balance · ${moneyExact(balance)}` : `Pay Full · ${moneyExact(balance)}`}
                  </button>
                </>
              )}
            </div>
          </Modal>
        );
      })()}
    </DashboardLayout>
  );
}
